import React, { useState, useEffect } from "react";
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButton,
  IonButtons,
  IonMenuButton,
  IonLoading,
} from "@ionic/react";
import { useHistory } from "react-router-dom";
import {
  Box,
  TextField,
  Button,
  Grid,
  Paper,
  Typography,
  Autocomplete,
} from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import { useForm, Controller } from "react-hook-form";
import { ledgerPostingReferences } from "../../../../common/consts";
import { useToast } from "../../../../context/ToastContext";
import { useLedger } from "../../../../hooks/useLedger";
import { Ledger, LedgerPosting } from "../../../../models/ledger.types";
// import { Ledger, LedgerPosting } from "../../types/ledger.types";
// import { useLedger } from "../../hooks/useLedger";
// import { useToast } from "../../contexts/ToastContext";
// import { ledgerPostingReferences } from "../../utils/consts";


const ManualLedgerPosting: React.FC = () => {
  const history = useHistory();
  const { ledgers, loading, fetchLedgers, postLedgerAccount } = useLedger();
  const { showError } = useToast();
  const [transactionDate] = useState<Dayjs>(dayjs());
  const [debitLedger, setDebitLedger] = useState<Ledger | null>(null);
  const [creditLedger, setCreditLedger] = useState<Ledger | null>(null);
  const [debitSearchInput, setDebitSearchInput] = useState("");
  const [creditSearchInput, setCreditSearchInput] = useState("");
  const [debitNotFound, setDebitNotFound] = useState(false);
  const [creditNotFound, setCreditNotFound] = useState(false);

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<LedgerPosting>({
    defaultValues: {
      amount: 0,
      desp: "",
      reference: "",
      referenceDetail: "",
    },
  });

  useEffect(() => {
    fetchLedgers();
  }, [fetchLedgers]);

  const handleDebitChange = (event: any, newValue: Ledger | null) => {
    setDebitLedger(newValue);
    setDebitNotFound(false);
    if (event?.target?.value && !newValue) {
      setDebitSearchInput(event.target.value);
      // Check if input doesn't match any ledger
      const matches = ledgers.filter(
        (ledger) =>
          ledger.name
            ?.toLowerCase()
            .includes(event.target.value.toLowerCase()) ||
          ledger.account_code
            ?.toLowerCase()
            .includes(event.target.value.toLowerCase())
      );
      if (matches.length === 0) {
        setDebitNotFound(true);
      }
    } else {
      setDebitSearchInput("");
    }
  };

  const handleCreditChange = (event: any, newValue: Ledger | null) => {
    setCreditLedger(newValue);
    setCreditNotFound(false);
    if (event?.target?.value && !newValue) {
      setCreditSearchInput(event.target.value);
      // Check if input doesn't match any ledger
      const matches = ledgers.filter(
        (ledger) =>
          ledger.name
            ?.toLowerCase()
            .includes(event.target.value.toLowerCase()) ||
          ledger.account_code
            ?.toLowerCase()
            .includes(event.target.value.toLowerCase())
      );
      if (matches.length === 0) {
        setCreditNotFound(true);
      }
    } else {
      setCreditSearchInput("");
    }
  };

  const onSubmit = async (data: LedgerPosting) => {
    if (!debitLedger || !creditLedger) {
      showError("Please select both debit and credit ledgers");
      return;
    }

    if (!debitLedger.sn || !creditLedger.sn) {
      showError("Selected ledgers are missing serial numbers");
      return;
    }

    if (data.amount <= 0) {
      showError("Amount must be greater than 0");
      return;
    }

    const posting: LedgerPosting = {
      amount: data.amount,
      dr_sn: debitLedger.sn,
      cr_sn: creditLedger.sn,
      desp: data.desp,
      reference: data.reference || "",
      referenceDetail: data.referenceDetail || "",
    };

    const success = await postLedgerAccount(posting);
    if (success) {
      history.push("/core-accounting/general-ledger");
    }
  };

  return (
    <IonPage className = "pushDown">
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonMenuButton />
          </IonButtons>
          <IonTitle>Ledger Posting</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={() => history.goBack()}>Cancel</IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <Box sx={{ p: 2 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 3 }}>
              Posting Form - ({transactionDate.format("DD/MM/YYYY")})
            </Typography>

            <form onSubmit={handleSubmit(onSubmit)}>
              <Grid container spacing={3}>
                {/* Search Ledger (DEBIT) */}
                <Grid item xs={12} md={6}>
                  <Autocomplete
                    options={ledgers}
                    getOptionLabel={(option) =>
                      option.name || option.account_code || ""
                    }
                    value={debitLedger}
                    onChange={handleDebitChange}
                    inputValue={debitSearchInput}
                    onInputChange={(event, newInputValue) => {
                      setDebitSearchInput(newInputValue);
                      setDebitNotFound(false);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Search Ledger (DEBIT)*"
                        required
                        error={debitNotFound}
                        helperText={
                          debitNotFound
                            ? "No ledger found dr"
                            : errors.dr_sn?.message
                        }
                      />
                    )}
                    renderOption={(props, option) => (
                      <Box
                        component="li"
                        {...props}
                        key={option.id || option.sn}
                      >
                        <Box>
                          <Typography variant="body1">{option.name}</Typography>
                          {option.account_code && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Code: {option.account_code}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    )}
                    filterOptions={(options, state) => {
                      const inputValue = state.inputValue.toLowerCase();
                      return options.filter(
                        (option) =>
                          option.name?.toLowerCase().includes(inputValue) ||
                          option.account_code
                            ?.toLowerCase()
                            .includes(inputValue)
                      );
                    }}
                  />
                </Grid>

                {/* Search Ledger (CREDIT) */}
                <Grid item xs={12} md={6}>
                  <Autocomplete
                    options={ledgers}
                    getOptionLabel={(option) =>
                      option.name || option.account_code || ""
                    }
                    value={creditLedger}
                    onChange={handleCreditChange}
                    inputValue={creditSearchInput}
                    onInputChange={(_event, newInputValue) => {
                      setCreditSearchInput(newInputValue);
                      setCreditNotFound(false);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Search Ledger (CREDIT)*"
                        required
                        error={creditNotFound}
                        helperText={
                          creditNotFound
                            ? "No ledger found cr"
                            : errors.cr_sn?.message
                        }
                      />
                    )}
                    renderOption={(props, option) => (
                      <Box
                        component="li"
                        {...props}
                        key={option.id || option.sn}
                      >
                        <Box>
                          <Typography variant="body1">{option.name}</Typography>
                          {option.account_code && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Code: {option.account_code}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    )}
                    filterOptions={(options, state) => {
                      const inputValue = state.inputValue.toLowerCase();
                      return options.filter(
                        (option) =>
                          option.name?.toLowerCase().includes(inputValue) ||
                          option.account_code
                            ?.toLowerCase()
                            .includes(inputValue)
                      );
                    }}
                  />
                </Grid>

                {/* Transaction Description */}
                <Grid item xs={12} md={6}>
                  <Controller
                    name="desp"
                    control={control}
                    rules={{
                      required: "Transaction description is required",
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Transaction Description"
                        fullWidth
                        required
                        error={!!errors.desp}
                        helperText={errors.desp?.message}
                      />
                    )}
                  />
                </Grid>

                {/* Amount */}
                <Grid item xs={12} md={6}>
                  <Controller
                    name="amount"
                    control={control}
                    rules={{
                      required: "Amount is required",
                      min: {
                        value: 0.01,
                        message: "Amount must be greater than 0",
                      },
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        type="number"
                        label="Amount"
                        fullWidth
                        required
                        error={!!errors.amount}
                        helperText={errors.amount?.message}
                        onChange={(e) => {
                          const value = parseFloat(e.target.value) || 0;
                          field.onChange(value);
                        }}
                      />
                    )}
                  />
                </Grid>

                {/* References */}
                <Grid item xs={12} md={6}>
                  <Controller
                    name="reference"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="References"
                        fullWidth
                        select
                        SelectProps={{
                          native: true,
                        }}
                      >
                        <option value="">Select Reference</option>
                        {ledgerPostingReferences.map((ref) => (
                          <option key={ref.value} value={ref.value}>
                            {ref.label}
                          </option>
                        ))}
                      </TextField>
                    )}
                  />
                </Grid>

                {/* Reference Details */}
                <Grid item xs={12} md={6}>
                  <Controller
                    name="referenceDetail"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Reference Details"
                        fullWidth
                      />
                    )}
                  />
                </Grid>

                {/* Print Receipt Checkbox */}
                {/* <Grid item xs={12}>
                  <FormControlLabel
                    control={<Checkbox />}
                    label="Print Receipt"
                  />
                </Grid> */}

                {/* Submit Button */}
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: 2,
                    }}
                  >
                    <Button variant="outlined" onClick={() => history.goBack()}>
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={loading}
                    >
                      POST TRANSACTION
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </form>
          </Paper>
        </Box>
      </IonContent>
      <IonLoading isOpen={loading} message="Processing..." />
    </IonPage>
  );
};

export default ManualLedgerPosting;
